import {objectFromEntries} from '@benchling/js-utils';
import moment from 'moment';

import {assert} from 'Asserts';
import {NameTemplatePartNode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getOptionsForNameTemplatePartType} from 'name-templates/GetOptionsForNameTemplatePartType';
import {
  NameTemplatePart,
  NameTemplatePartUpsert,
  NameTemplatePartType,
  NameTemplateType,
  getSupportedNameTemplatePartTypes,
} from 'name-templates/NameTemplateConstants';
import {SubmenuContext} from 'name-templates/PartPredicatesUtil';
import {ClientSchemaField} from 'registry/field/FieldTable';
import SchemaFieldSelectorStore from 'registry/schema-field-selector/SchemaFieldSelectorStore';
import {FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {OptionData} from 'ui-library/menus/MenuConstants';

export function makeExampleForNameTemplate(
  nameTemplateParts: Array<NameTemplatePart>,
  schemaFields: Array<SchemaField | ClientSchemaField>
): string {
  return nameTemplateParts.map((part) => _makeExampleForPart(part, schemaFields)).join('');
}

// Only FIELD parts do a schema field lookup in _makeExampleForPart; other fieldId-bearing parts are safe to pass through.
export function filterPartsForActiveFields(
  nameTemplateParts: Array<NameTemplatePart>,
  activeSchemaFields: Array<SchemaField | ClientSchemaField>
): Array<NameTemplatePart> {
  const activeFieldIds = new Set(activeSchemaFields.map((f) => f.apiId));
  return nameTemplateParts.filter(
    (part) => part.type !== NameTemplatePartType.FIELD || activeFieldIds.has(part.fieldId)
  );
}

export function _makeExampleForPart(
  part: NameTemplatePart,
  schemaFields: Array<SchemaField | ClientSchemaField>
): string {
  // Uses override example value if one is provided by the name template part registry.
  // @ts-expect-error: Some name template parts have exampleValue
  if (part.hasOwnProperty('exampleValue') && part.exampleValue != null) {
    // @ts-expect-error: We've checked that exampleValue exists and is non-null
    return part.exampleValue;
  }

  switch (part.type) {
    case NameTemplatePartType.CREATED_AT_YEAR:
    case NameTemplatePartType.CONTAINER_CREATED_AT_YEAR:
    case NameTemplatePartType.FILL_YEAR:
      return String(new Date().getFullYear());
    case NameTemplatePartType.CREATED_AT_DATE:
    case NameTemplatePartType.CONTAINER_CREATED_AT_DATE:
      return moment(new Date()).format('YYYYMMDD').toString();
    case NameTemplatePartType.FILL_DATE:
      return moment(new Date()).format('YYYY-MM-DD').toString();
    case NameTemplatePartType.FIELD: {
      const field = schemaFields.find((f) => f.apiId === part.fieldId);
      assert(field != null, 'Expected to find field ID in schemaFields array.');
      return makeExampleForField(field);
    }
    case NameTemplatePartType.CHILD_ENTITY_LOT_NUMBER:
    case NameTemplatePartType.SAMPLE_LOT_NUMBER:
      return '003';
    case NameTemplatePartType.LINKED_BIOENTITY_REGISTRY_IDENTIFIER:
      return 'p012';
    case NameTemplatePartType.HOISTED_FIELD:
      return 'HoistedValue';
    case NameTemplatePartType.REGISTRY_IDENTIFIER_NUMBER:
      return '012';
    case NameTemplatePartType.REGISTRY_IDENTIFIER_NUMBER_BASE_26:
      return 'AAO';
    case NameTemplatePartType.BARCODE:
      return 'tube456';
    case NameTemplatePartType.BARCODE_NUMBER:
      return '456';
    case NameTemplatePartType.QUANTITY_UNITS:
      return 'mL';
    case NameTemplatePartType.CONCENTRATION_UNITS:
      return 'g/mL';
    case NameTemplatePartType.CONCENTRATION_VALUE:
    case NameTemplatePartType.QUANTITY_VALUE:
      return '5';
    case NameTemplatePartType.CONTAINED_ENTITY_NAME:
      return 'entityname';
    case NameTemplatePartType.PARENT_STORABLE_BARCODE:
      return 'box789';
    case NameTemplatePartType.PARENT_STORABLE_NAME:
      return 'boxname';
    case NameTemplatePartType.POSITION:
      return 'A1';
    case NameTemplatePartType.PROJECT:
      return 'project';
    case NameTemplatePartType.RESTRICTION_STATUS:
      return 'Restricted';
    case NameTemplatePartType.SALT_CODES:
      return 'PH';
    case NameTemplatePartType.TEXT:
    case NameTemplatePartType.SEPARATOR:
      return part.text;
    default:
      assert(false, `Unhandled part type: ${part.type}`);
  }
}

export function makeExampleForField(field: SchemaField | ClientSchemaField): string {
  switch (field.fieldType) {
    case FieldType.INTEGER:
      return String(73);
    case FieldType.FLOAT:
      return String(12.1);
    case FieldType.SELECTOR: {
      if (field.schemaFieldSelectorId) {
        const selector = SchemaFieldSelectorStore.getSelectorById(field.schemaFieldSelectorId);
        if (selector && selector.schemaFieldSelectorOptions.length > 0) {
          return selector.schemaFieldSelectorOptions[0].name;
        }
      }
      break;
    }
    default:
      break;
  }
  return `sample${field.name.replace(/\s/g, '').toLowerCase()}`;
}

export function graphQLNameTemplatePartsToProps(
  graphQLNameTemplateParts: Array<NameTemplatePartNode>
): Array<NameTemplatePart> {
  return graphQLNameTemplateParts.map(
    (graphQLNameTemplatePart) =>
      objectFromEntries(
        Object.entries(graphQLNameTemplatePart).filter(([k, v]) => v != null && k !== '__typename')
      ) as NameTemplatePart
  );
}

export function getSupportedNameTemplatePartOptions(
  nameTemplateType: NameTemplateType,
  schemaFields: Array<SchemaField | ClientSchemaField>,
  appendPartFunc: (part: NameTemplatePart) => void,
  possibleNameTemplatePartOptions: Array<NameTemplatePartNode>,
  submenuContext?: SubmenuContext
): Array<OptionData> {
  const supportedNamePartSections = getSupportedNameTemplatePartTypes(
    nameTemplateType,
    submenuContext ?? {itemType: null}
  );
  const options: Array<OptionData> = [];
  for (const namePartSection of supportedNamePartSections) {
    if (namePartSection.sectionHeader != null) {
      options.push({
        name: namePartSection.sectionHeader,
        isHeader: true,
        className: 'schema-section',
      });
    }
    namePartSection.namePartTypes.forEach((supportedNamePart) => {
      options.push(
        ...getOptionsForNameTemplatePartType(
          schemaFields,
          possibleNameTemplatePartOptions,
          appendPartFunc,
          supportedNamePart
        )
      );
    });
    if (namePartSection.submenus != null) {
      for (const submenu of namePartSection.submenus) {
        const submenuOptions = submenu.namePartTypes.flatMap((supportedNamePart) =>
          getOptionsForNameTemplatePartType(
            schemaFields,
            possibleNameTemplatePartOptions,
            appendPartFunc,
            supportedNamePart
          )
        );

        options.push({
          name: submenu.sectionHeader,
          submenu: submenuOptions.length > 0 ? [submenuOptions] : [],
          disabled: submenuOptions.length === 0,
          disabledMessage: 'No options available',
          value: () => null,
          dataTestAttrs: {
            component: 'SetNameTemplateModal',
            element: `submenu-${submenu.sectionHeader}`,
          },
        });
      }
    }
  }
  return options;
}

export function stripExcessKeys({
  type,
  fieldId,
  text,
  datetimeFormat,
  typeSpecificConfiguration,
}: NameTemplatePartUpsert): NameTemplatePartUpsert {
  return {
    type,
    fieldId,
    text,
    datetimeFormat,
    typeSpecificConfiguration,
  };
}
